import { fetchAllCriteria, fetchQuery } from '@/features/AudienceManager/data';
import router from '@/router';
const defaultState = () => ({
    isConditionEditMode: false,
    editingCondition: {},
    isLoadingCompositionStage: false,
    allCriteriaOptions: [],
    isConditionFormVisible: false,
    include: [],
    exclude: [],
    activeGroupIndex: null,
    invalidConditions: {},
    selectedCriteriaDetails: {
        include: {},
        exclude: {},
    },
});
const audienceManagerComposition = {
    namespaced: true,
    state: defaultState(),
    getters: {
        isConditionEditMode: state => state.isConditionEditMode,
        selectedCriteriaDetails: state => (groupType, groupIndex) => state.selectedCriteriaDetails[groupType]?.[groupIndex] || null,
        editingCondition: state => state.editingCondition,
        query: state => ({ include: [...state.include], exclude: [...state.exclude] }),
        hasNoAudienceData: state => state.include.every(group => !group.length) && state.exclude.every(group => !group.length),
        hasAudienceData: state => state.include.some(group => group.length > 0) || state.exclude.some(group => group.length > 0),
        isLoadingCompositionStage: state => state.isLoadingCompositionStage,
        allCriteriaOptions: state => state.allCriteriaOptions,
        isConditionFormVisible: state => state.isConditionFormVisible,
        activeGroupIndex: state => state.activeGroupIndex,
        conditionHasError: state => (groupType, groupIndex) => !!state.invalidConditions[groupType]?.[groupIndex],
    },
    actions: {
        setConditionEditMode({ commit }, isEdit) {
            commit('SET_CONDITION_EDIT_MODE', isEdit);
        },
        setEditingCondition({ commit }, payload) {
            commit('SET_EDITING_CONDITION', payload);
        },
        saveCondition({ commit, state, dispatch }, { groupIndex, groupType, isConditionEditMode }) {
            dispatch('audienceManager/triggerDebounceReset', null, { root: true });
            commit('audienceManager/IS_CONDITION_ADDED', true, { root: true });
            const editingGroup = state.editingCondition?.[groupType]?.[groupIndex];
            const draftCondition = editingGroup?.draftCondition;
            const conditionIndex = editingGroup?.conditionIndex;
            if (groupIndex === null || draftCondition === undefined)
                return;
            if (isConditionEditMode) {
                if (typeof conditionIndex === 'number' && conditionIndex >= 0) {
                    commit('UPDATE_CONDITION', { groupType, groupIndex, existingIndex: conditionIndex });
                }
            }
            else {
                commit('ADD_NEW_CONDITION', { groupType, groupIndex });
            }
        },
        fetchCriteriaOptions({ commit, dispatch, getters, rootGetters }, mode) {
            commit('SET_LOADING_COMPOSITION_STAGE', true);
            commit('audienceManager/UPDATE_COUNT_LOADING', true, { root: true });
            fetchAllCriteria(mode).then((response) => {
                const isDuplicate = rootGetters['audienceManager/isDuplicate'];
                commit('SET_CRITERIA_OPTIONS', response.data);
                const audienceId = rootGetters['audienceManager/getAudienceId'];
                if (isDuplicate || getters.isView) {
                    fetchQuery(audienceId)
                        .then((response) => {
                        if (response?.success) {
                            const { include, exclude } = response.data.query;
                            if (router.currentRoute.value.name !== 'AudienceDuplicateNew') {
                                commit('audienceManager/SET_AUDIENCE_NAME', response.data.name, { root: true });
                            }
                            dispatch('resetInclude', include || []);
                            dispatch('resetExclude', exclude || []);
                        }
                    })
                        .finally(() => {
                        commit('SET_LOADING_COMPOSITION_STAGE', false);
                        commit('audienceManager/UPDATE_COUNT_LOADING', false, { root: true });
                        if (isDuplicate) {
                            commit('TOGGLE_CONDITION_FORM', {
                                isVisible: true,
                                groupIndex: 0,
                            });
                        }
                        dispatch('audienceManager/updateCount', null, { root: true });
                    });
                }
                else {
                    commit('SET_LOADING_COMPOSITION_STAGE', false);
                    commit('audienceManager/UPDATE_COUNT_LOADING', false, { root: true });
                    dispatch('addGroup', { isInclude: true });
                }
            });
        },
        addGroup({ commit, getters }, { isInclude }) {
            const groupIndex = getters.query[isInclude ? 'include' : 'exclude'].length;
            commit('ADD_GROUP', isInclude);
            commit('TOGGLE_CONDITION_FORM', {
                isVisible: true,
                groupIndex: Number(groupIndex),
            });
        },
        updateDraftCondition({ commit, state, dispatch }, payload) {
            const { groupType, groupIndex, data } = payload;
            dispatch('audienceManager/triggerDebounceReset', null, { root: true });
            if (!state.editingCondition[groupType]) {
                state.editingCondition[groupType] = {};
            }
            if (!state.editingCondition[groupType][groupIndex]) {
                commit('INITIALIZE_DRAFT_CONDITION', payload);
            }
            const currentDraft = JSON.stringify(state.editingCondition[groupType][groupIndex].draftCondition);
            const newDraft = JSON.stringify({
                ...state.editingCondition[groupType][groupIndex].draftCondition,
                ...data,
            });
            if (currentDraft !== newDraft) {
                commit('UPDATE_DRAFT_CONDITION', payload);
            }
        },
        updateSelectedCriteria({ commit, getters }, { criteriaKey, groupType, groupIndex }) {
            if (!criteriaKey) {
                commit('UPDATE_SELECTED_CRITERIA_DETAILS', { groupType, groupIndex, criteria: null });
                return;
            }
            const criteriaOptions = getters.allCriteriaOptions || [];
            const newSelection = criteriaOptions
                .flatMap(category => category.columns)
                .find(criteria => criteria.key === criteriaKey);
            if (newSelection) {
                commit('UPDATE_SELECTED_CRITERIA_DETAILS', { groupType, groupIndex, criteria: newSelection });
            }
        },
        removeGroup({ state, commit, dispatch }, payload) {
            const targetArray = payload.isInclude ? state.include : state.exclude;
            if (targetArray.length >= 1) {
                commit('REMOVE_GROUP', payload);
                dispatch('audienceManager/updateCount', null, { root: true });
            }
        },
        removeCondition({ commit }, payload) {
            commit('audienceManager/IS_CONDITION_ADDED', true, { root: true });
            commit('REMOVE_CONDITION', payload);
        },
        resetEditingCondition({ commit }) {
            commit('RESET_EDITING_CONDITION');
        },
        resetCompositionState({ commit }) {
            commit('RESET_COMPOSITION_STATE');
        },
        resetInclude({ commit }, value) {
            commit('RESET_INCLUDE', value);
        },
        resetExclude({ commit }, value) {
            commit('RESET_EXCLUDE', value);
        },
        clearEditingConditionMode({ commit }) {
            commit('SET_CONDITION_EDIT_MODE', false);
        },
        toggleConditionForm({ commit }, payload) {
            commit('TOGGLE_CONDITION_FORM', payload);
        },
    },
    mutations: {
        SET_CONDITION_VALIDITY(state, { groupType, groupIndex, isValid }) {
            if (!state.invalidConditions[groupType]) {
                state.invalidConditions[groupType] = {};
            }
            state.invalidConditions[groupType][groupIndex] = !isValid;
        },
        CLEAR_CONDITION_VALIDITY(state, { groupType, groupIndex }) {
            if (state.invalidConditions[groupType]) {
                delete state.invalidConditions[groupType][groupIndex];
            }
        },
        INITIALIZE_DRAFT_CONDITION(state, { groupType, groupIndex }) {
            if (!state.editingCondition[groupType]) {
                state.editingCondition[groupType] = {};
            }
            state.editingCondition[groupType][groupIndex] = {
                draftCondition: {
                    column: '',
                    operator: '',
                    value: [],
                    referenceObjects: {},
                    f_operator: '',
                    f_value: '',
                    f_type: '',
                },
            };
        },
        SET_CONDITION_EDIT_MODE(state, isEdit) {
            state.isConditionEditMode = isEdit;
        },
        SET_LOADING_COMPOSITION_STAGE(state, isLoading) {
            state.isLoadingCompositionStage = isLoading;
        },
        SET_EDITING_CONDITION(state, { groupType, groupIndex, rowId, draftCondition }) {
            if (!state.editingCondition[groupType]) {
                state.editingCondition[groupType] = {};
            }
            if (state.editingCondition[groupType][groupIndex]) {
                state.editingCondition[groupType][groupIndex] = {
                    groupType,
                    groupIndex,
                    conditionIndex: rowId,
                    draftCondition: JSON.parse(JSON.stringify(draftCondition)) || {},
                };
            }
            else {
                if (!state.editingCondition[groupType][groupIndex]?.draftCondition) {
                    state.editingCondition[groupType][groupIndex] = {
                        draftCondition: {},
                    };
                }
                Object.assign(state.editingCondition[groupType][groupIndex].draftCondition, draftCondition);
            }
        },
        SET_CRITERIA_OPTIONS(state, allCriteriaOptions) {
            state.allCriteriaOptions = allCriteriaOptions;
        },
        ADD_GROUP(state, isInclude) {
            state[isInclude ? 'include' : 'exclude'].push([]);
        },
        ADD_NEW_CONDITION(state, { groupType, groupIndex }) {
            state[groupType][groupIndex].push({ ...state.editingCondition[groupType][groupIndex].draftCondition });
        },
        UPDATE_CONDITION(state, { groupType, groupIndex, existingIndex }) {
            state[groupType][groupIndex][existingIndex] = { ...state.editingCondition[groupType][groupIndex].draftCondition };
        },
        UPDATE_DRAFT_CONDITION(state, { groupType, groupIndex, data }) {
            if (state.editingCondition[groupType] && state.editingCondition[groupType][groupIndex]) {
                Object.assign(state.editingCondition[groupType][groupIndex].draftCondition, data);
            }
        },
        UPDATE_SELECTED_CRITERIA_DETAILS(state, { groupType, groupIndex, criteria }) {
            if (!state.selectedCriteriaDetails[groupType]) {
                state.selectedCriteriaDetails[groupType] = {};
            }
            state.selectedCriteriaDetails[groupType][groupIndex] = criteria;
        },
        REMOVE_GROUP(state, payload) {
            const targetArray = payload.isInclude ? state.include : state.exclude;
            targetArray.splice(payload.groupIndex, 1);
        },
        REMOVE_CONDITION(state, payload) {
            const groupIndex = payload.groupIndex;
            const groupType = payload.groupType;
            const targetGroup = state[groupType][groupIndex];
            targetGroup.splice(payload.rowId, 1);
        },
        RESET_COMPOSITION_STATE(state) {
            Object.assign(state, defaultState());
        },
        RESET_EDITING_CONDITION(state, { groupType, groupIndex }) {
            if (state.editingCondition[groupType]?.[groupIndex]) {
                delete state.editingCondition[groupType][groupIndex];
            }
        },
        RESET_INCLUDE(state, newValue) {
            state.include = newValue;
        },
        RESET_EXCLUDE(state, newValue) {
            state.exclude = newValue;
        },
        UPDATE_ACTIVE_GROUP_INDEX(state, index) {
            state.activeGroupIndex = index;
        },
        TOGGLE_CONDITION_FORM(state, payload) {
            state.isConditionFormVisible = payload.isVisible;
            state.activeGroupIndex = payload.isVisible ? payload.groupIndex : null;
        },
    },
};
export default audienceManagerComposition;
